import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { RefreshCw, UserMinus } from 'lucide-react';
import { API_BASE_URL } from '../../services/apiClient';

const REASON_LABELS = {
  admin_canceled: 'Cancelado por admin',
  too_expensive: 'Es muy caro / no le alcanza',
  not_using_it: 'No le sacaba provecho',
  no_time: 'Le faltó tiempo para usarlo',
  missing_feature: 'Le faltó una función',
  no_longer_selling: 'Ya no vende en Mercado Libre',
  other: 'Otro',
};

const TIER_LABELS = { trial: 'Prueba', entrepreneur: 'Emprendedor', pro: 'Pro', elite: 'Elite' };

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-CO', { year: 'numeric', month: 'short', day: 'numeric' });
};

export default function CancelledSubscriptionsPanel({ authToken }) {
  const [cancellations, setCancellations] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${authToken}` };

  const fetchCancellations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/cancelled-subscriptions`, { headers });
      setCancellations(res.data.cancellations || []);
    } catch (err) {
      console.error('[CancelledSubscriptionsPanel] Error fetching cancellations:', err);
      toast.error('No se pudieron cargar las cancelaciones');
    } finally {
      setLoading(false);
    }
  }, [authToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchCancellations();
  }, [fetchCancellations]);

  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <UserMinus className="w-5 h-5 text-red-400" />
          Suscripciones canceladas ({cancellations.length})
        </h3>
        <Button variant="outline" size="sm" onClick={fetchCancellations} disabled={loading} className="border-slate-700 text-slate-300">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {loading && cancellations.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="w-6 h-6 text-slate-500 animate-spin" />
        </div>
      ) : cancellations.length === 0 ? (
        <p className="text-slate-500 text-sm text-center py-8">No hay suscripciones canceladas todavía.</p>
      ) : (
        <div className="max-h-[65vh] overflow-y-auto space-y-2">
          {cancellations.map((c) => (
            <div key={c.user_id} className="bg-slate-950/50 border border-slate-800 rounded-lg p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-white font-medium">{c.name || c.email || c.user_id}</p>
                  <p className="text-xs text-slate-500">{c.email}</p>
                </div>
                <div className="text-right shrink-0 space-y-1">
                  <div className="flex items-center gap-1.5 justify-end">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-300">
                      {TIER_LABELS[c.tier] || c.tier || '—'}
                    </span>
                    {c.status === 'pending_period_end' ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">Pendiente</span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-400">Finalizada</span>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-500">
                    {c.status === 'pending_period_end'
                      ? `Activa hasta ${formatDate(c.current_period_end)}`
                      : `Cancelada: ${formatDate(c.canceled_at)}`}
                  </p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-800">
                <p className="text-sm text-slate-300">
                  <span className="text-slate-500">Motivo: </span>
                  {c.reason ? (REASON_LABELS[c.reason] || c.reason) : 'No especificado (cancelada fuera del flujo)'}
                </p>
                {c.description && (
                  <p className="text-sm text-slate-400 mt-1 italic">"{c.description}"</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
